import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
    Alert,
    Checkbox,
    FormControlLabel,
    Radio,
    RadioGroup,
    TableCell,
    TableRow,
    TextField,
} from "@mui/material";
import Swal from "sweetalert2";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Layout from "../../Components/Layout";
import IndexApp from "../../Components/IndexApp";
import { BrandContent, useTailwindBrand } from "../../Components/shared";
import ButtonField from "../../Components/Ui/ButtonField";
import TableLayout from "../../Components/Ui/TableLayout";
import config from "../../config/config";
import { getActiveBrandConfig } from "../../config/environment.config";
import AdminMailRepository, { EligibleTeachr } from "../../Repositories/AdminMailRepository";

// Toolbar volontairement réduite : gras, italique, listes — le HTML généré est
// injecté tel quel dans le template mail du backend ({{ text | raw }}).
const QUILL_MODULES = {
    toolbar: [["bold", "italic"], [{ list: "ordered" }, { list: "bullet" }], ["clean"]],
};
const QUILL_FORMATS = ["bold", "italic", "list", "bullet"];

const HEAD_CELLS = [
    { id: 0, label: "", disable: false, numeric: false },
    { id: 1, label: "Nom", disable: false, numeric: false },
    { id: 2, label: "Prénom", disable: false, numeric: false },
    { id: 3, label: "Email", disable: false, numeric: false },
    { id: 4, label: "Marque", disable: false, numeric: false },
];

/**
 * Interface admin – envoi de mails groupés aux professeurs éligibles
 * (AE validés, SAP signé, au moins un cours déclaré sur les 6 derniers mois).
 * L'accès est contrôlé côté backend (ADMIN_TEACHR_ID) ; côté front la page
 * n'est rendue que pour le compte admin configuré.
 */
const AdminBulkMailPage = () => {
    const { classes, common } = useTailwindBrand();
    const teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);

    const [eligible, setEligible] = useState<EligibleTeachr[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [recipientMode, setRecipientMode] = useState<"all" | "selection">("all");
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sending, setSending] = useState(false);

    const loadEligible = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await AdminMailRepository.getEligibleTeachrs();
            setEligible(data.teachrs ?? []);
        } catch (e) {
            setError("Impossible de charger la liste des professeurs éligibles. Vérifiez vos droits d'accès.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadEligible();
    }, [loadEligible]);

    const filteredTeachrs = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return eligible;
        return eligible.filter((t) =>
            `${t.firstName} ${t.lastName} ${t.mail}`.toLowerCase().includes(term)
        );
    }, [eligible, search]);

    const pagedTeachrs = useMemo(
        () => filteredTeachrs.slice((page - 1) * rowsPerPage, page * rowsPerPage),
        [filteredTeachrs, page, rowsPerPage]
    );

    if (teachr && teachr.id !== config.adminId) {
        return <Navigate to={`/${getActiveBrandConfig()}/tableau-de-bord`} replace />;
    }

    // Quill renvoie "<p><br></p>" quand l'éditeur est vidé : on le traite comme vide.
    const isBodyEmpty = !body || body.replace(/<(.|\n)*?>/g, "").trim() === "";
    const recipientCount = recipientMode === "all" ? eligible.length : selectedIds.length;
    const canSend = !sending && !loading && subject.trim() !== "" && !isBodyEmpty && recipientCount > 0;

    const toggleSelected = (id: number) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((selected) => selected !== id) : [...prev, id]
        );
    };

    const allFilteredSelected =
        filteredTeachrs.length > 0 && filteredTeachrs.every((t) => selectedIds.includes(t.id));

    const toggleSelectAllFiltered = () => {
        if (allFilteredSelected) {
            const filteredIds = filteredTeachrs.map((t) => t.id);
            setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
        } else {
            setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredTeachrs.map((t) => t.id)])));
        }
    };

    const handleSend = async () => {
        const { isConfirmed } = await Swal.fire({
            icon: "question",
            title: `Envoyer ce mail à ${recipientCount} professeur${recipientCount > 1 ? "s" : ""} ?`,
            html: `<b>Objet :</b> ${subject}<br/><br/>Le mail sera envoyé ${
                recipientMode === "all"
                    ? "à <b>tous les professeurs éligibles</b>."
                    : "aux professeurs <b>sélectionnés manuellement</b>."
            }`,
            showCancelButton: true,
            confirmButtonText: "Oui, envoyer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#0054A3",
        });
        if (!isConfirmed) return;

        setSending(true);
        try {
            const result = await AdminMailRepository.sendBulkMail({
                subject: subject.trim(),
                body,
                recipientMode,
                teachrIds: recipientMode === "selection" ? selectedIds : undefined,
            });
            await Swal.fire({
                icon: "success",
                title: "Mail envoyé !",
                text: `Le mail a été envoyé à ${result.sentCount} professeur${result.sentCount > 1 ? "s" : ""}.`,
                confirmButtonColor: "#0054A3",
            });
            setSubject("");
            setBody("");
            setSelectedIds([]);
        } catch (e: any) {
            Swal.fire({
                icon: "error",
                title: "Échec de l'envoi",
                text: e?.message ?? "Une erreur est survenue lors de l'envoi du mail.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <IndexApp
            index={1}
            teachr={teachr}
            component={
                <Layout
                    header={
                        <div className="px-1">
                            <BrandContent
                                tag="h1"
                                children="Envoi de mails aux professeurs"
                                type="homeSubtitle"
                                className={`${common.subtitle} mb-2 ${classes.primaryText}`}
                            />
                            <BrandContent
                                tag="div"
                                children="Rédigez un mail et envoyez-le à tous les professeurs éligibles (auto-entrepreneurs validés, SAP signé, au moins un cours déclaré sur les 6 derniers mois) ou à une sélection."
                                type="description"
                                className={`lg:w-3/4 ${common.body} ${classes.text}`}
                            />
                        </div>
                    }
                >
                    <div className="space-y-6">
                        {error && <Alert severity="error">{error}</Alert>}

                        <div className="border border-gray-200 shadow-sm rounded-md bg-white p-4 space-y-4">
                            <p className={`font-bold ${classes.primaryText}`}>Message</p>
                            <TextField
                                fullWidth
                                label="Objet du mail"
                                value={subject}
                                onChange={(event) => setSubject(event.target.value)}
                                inputProps={{ maxLength: 150 }}
                            />
                            <div className="bg-white">
                                <ReactQuill
                                    theme="snow"
                                    value={body}
                                    onChange={setBody}
                                    modules={QUILL_MODULES}
                                    formats={QUILL_FORMATS}
                                    placeholder="Contenu du mail… (le « Bonjour Prénom, » et la signature sont ajoutés automatiquement)"
                                    style={{ minHeight: "180px" }}
                                />
                            </div>
                        </div>

                        <div className="border border-gray-200 shadow-sm rounded-md bg-white p-4 space-y-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className={`font-bold ${classes.primaryText}`}>Destinataires</p>
                                {!loading && (
                                    <span className="text-sm text-gray-600">
                                        {eligible.length} professeur{eligible.length > 1 ? "s" : ""} éligible{eligible.length > 1 ? "s" : ""}
                                    </span>
                                )}
                            </div>
                            <RadioGroup
                                row
                                value={recipientMode}
                                onChange={(event) => setRecipientMode(event.target.value as "all" | "selection")}
                            >
                                <FormControlLabel
                                    value="all"
                                    control={<Radio />}
                                    label={`Tous les professeurs éligibles (${eligible.length})`}
                                />
                                <FormControlLabel
                                    value="selection"
                                    control={<Radio />}
                                    label="Sélection manuelle"
                                />
                            </RadioGroup>

                            {loading && <p className={common.body}>Chargement des professeurs éligibles…</p>}

                            {!loading && recipientMode === "selection" && (
                                <div className="space-y-2">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <TextField
                                            size="small"
                                            label="Rechercher (nom, email…)"
                                            value={search}
                                            onChange={(event) => {
                                                setSearch(event.target.value);
                                                setPage(1);
                                            }}
                                        />
                                        <div className="flex items-center gap-4">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={allFilteredSelected}
                                                        indeterminate={!allFilteredSelected && filteredTeachrs.some((t) => selectedIds.includes(t.id))}
                                                        onChange={toggleSelectAllFiltered}
                                                    />
                                                }
                                                label={search.trim() ? "Tout sélectionner (résultats filtrés)" : "Tout sélectionner"}
                                            />
                                            <span className={`text-sm font-semibold ${classes.primaryText}`}>
                                                {selectedIds.length} sélectionné{selectedIds.length > 1 ? "s" : ""}
                                            </span>
                                        </div>
                                    </div>
                                    <TableLayout
                                        headCells={HEAD_CELLS}
                                        page={page}
                                        rowsPerPage={rowsPerPage}
                                        count={filteredTeachrs.length}
                                        handleChangePage={(_, newPage) => setPage(newPage + 1)}
                                        handleChangeRowsPerPage={(event) => {
                                            setRowsPerPage(parseInt(event.target.value, 10));
                                            setPage(1);
                                        }}
                                        size="small"
                                        Children={
                                            pagedTeachrs.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center">
                                                        Aucun professeur ne correspond à la recherche.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                pagedTeachrs.map((t) => (
                                                    <TableRow
                                                        key={t.id}
                                                        hover
                                                        onClick={() => toggleSelected(t.id)}
                                                        sx={{ cursor: "pointer" }}
                                                        selected={selectedIds.includes(t.id)}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox checked={selectedIds.includes(t.id)} />
                                                        </TableCell>
                                                        <TableCell>{t.lastName}</TableCell>
                                                        <TableCell>{t.firstName}</TableCell>
                                                        <TableCell>{t.mail}</TableCell>
                                                        <TableCell>{t.brand ?? "teachr"}</TableCell>
                                                    </TableRow>
                                                ))
                                            )
                                        }
                                    />
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end">
                            <ButtonField
                                variant="contained"
                                label={`Envoyer${recipientCount > 0 ? ` à ${recipientCount} professeur${recipientCount > 1 ? "s" : ""}` : ""}`}
                                loading={sending}
                                disabled={!canSend}
                                onClick={handleSend}
                            />
                        </div>
                    </div>
                </Layout>
            }
        />
    );
};

export default AdminBulkMailPage;
